import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { StringParam, useQueryParams } from 'use-query-params'

import classes from '../SearchPage.module.scss'

import { useInfiniteQuery, useQueryCountryCodeValidation } from '@percent/workplace-giving/common/hooks'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { Page } from '@percent/workplace-giving/common/components/Layout/Page'
import { SearchPageHeader } from '@percent/workplace-giving/common/components/SearchPageHeader/SearchPageHeader'
import { mapInfiniteData } from '@percent/workplace-giving/utils/mapInfiniteData'
import { useInfiniteScroll } from '@percent/workplace-giving/common/hooks/useInfiniteScroll/useInfinteScroll'
import { Flex, IllustratedMessage } from '@percent/lemonade'
import { searchFundraisers } from '@percent/workplace-giving/api/search/searchFundraisers/searchFundraisers'
import { FundraiserCardSkeleton } from '@percent/workplace-giving/common/components/FundraiserCard/FundraiserCardSkeleton'
import { FundraiserCard } from '@percent/workplace-giving/common/components/FundraiserCard/FundraiserCard'
import { SwitchToGlobalResults } from '@percent/workplace-giving/common/components/SwitchToGlobalResults/SwitchToGlobalResults'

export function FundraisersSearchPage() {
  const { t } = useTranslation()
  const { track } = useAnalytics()

  const [query, setQuery] = useQueryParams({
    query: StringParam,
    country: StringParam
  })

  const { query: searchedQuery, country } = query

  const countryCode = useQueryCountryCodeValidation({ code: typeof country !== 'string' ? undefined : country })
  const prevCountryCodeRef = useRef<string>(countryCode)

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isError } = useInfiniteQuery(
    ['searchFundraisers', { query: searchedQuery, pageSize: 10, targetCountries: countryCode }],
    searchFundraisers,
    {
      staleTime: 0
    }
  )

  const totalResults = data && data.pages.length > 0 ? data.pages[0].totalResults : 0
  const { ref } = useInfiniteScroll(fetchNextPage)

  const mappedData = mapInfiniteData(data)

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [query])

  const onCountryChange = (newCountry: string) => {
    setQuery({ ...query, country: newCountry })
  }

  const onQueryChange = (q: string) => {
    setQuery({ ...query, query: q })
  }

  const switchToGlobal = () => {
    if (countryCode) {
      prevCountryCodeRef.current = countryCode
    }
    setQuery({ ...query, country: '' })
  }

  const switchToCountry = () => {
    const prevCountryCode = prevCountryCodeRef.current

    if (!prevCountryCode) return
    setQuery({ ...query, country: prevCountryCode })
    prevCountryCodeRef.current = ''
  }

  useEffect(() => {
    if (searchedQuery?.trim().length) {
      track({
        event: 'Search Fundraisers',
        properties: {
          origin: 'Fundraisers Search Page',
          countryCode
        }
      })
    }
  }, [searchedQuery, countryCode, track])

  return (
    <Page testId="fundraisers-search-page" fullWidthContent>
      <SearchPageHeader
        query={searchedQuery || ''}
        countryCode={countryCode}
        onCountryChange={onCountryChange}
        onQueryChange={onQueryChange}
        emptyQueryPageTitle={t('workplace_giving.search.exploreFundraisers')}
        breadcrumbsPageName={t('workplace_giving.search.breadcrumbs.fundraisers')}
        searchBarPlaceholder={t('workplace_giving.search.placeholder.fundraisers')}
        allowEmptyQuery
        hideAiPrompt
      />
      <Flex justify="flex-end" align="center" wrap="wrap" gap="xs" mb="md">
        <SwitchToGlobalResults
          totalResults={totalResults}
          currentCountryCode={countryCode}
          prevCountryCode={prevCountryCodeRef.current}
          switchToGlobal={switchToGlobal}
          switchToCountry={switchToCountry}
          isLoading={isFetching}
        />
      </Flex>
      {isLoading ? (
        <div className={classes.ResultsWrapper}>
          <FundraiserCardSkeleton />
          <FundraiserCardSkeleton />
          <FundraiserCardSkeleton />
          <FundraiserCardSkeleton />
          <FundraiserCardSkeleton />
        </div>
      ) : null}
      {!isLoading && !isError ? (
        <div className={classes.ResultsWrapper}>
          {mappedData?.pages?.map(page =>
            page.map(f => (
              <FundraiserCard
                key={f.fundraiser.id}
                fundraiser={{
                  id: f.fundraiser.id,
                  imageUrl: f.fundraiser.page.imageUrl!,
                  title: f.fundraiser.page.title,
                  goal: f.fundraiser.campaign.goal,
                  total: f.donationTotal,
                  organisation: f.organisation
                }}
              />
            ))
          )}
          {hasNextPage && (
            <div ref={ref}>
              <FundraiserCardSkeleton />
            </div>
          )}
        </div>
      ) : null}
      <div className={classes.InfosWrapper}>
        {totalResults === 0 && !isFetching && !isError ? (
          <IllustratedMessage
            illustration="no-results"
            title={t('workplace_giving.search.noResultsGlobalTitle', {
              searchTerm: query,
              interpolation: { escapeValue: false }
            })}
          />
        ) : null}
        {isError && (
          <IllustratedMessage
            illustration="general-error"
            title={t('workplace_giving.errors.somethingWentWrong')}
            description={t('workplace_giving.errors.tryReloadingThePage')}
          />
        )}
      </div>
    </Page>
  )
}
